import { supabase } from "~/integrations/supabase/client";
import type {
  Pallet,
  PalletBatch,
  PalletBatchFormData,
  PalletFilters,
  PalletStatus,
  Position,
  RelocationResult,
} from "~/types/wms";

export interface PalletWithDetails extends Pallet {
  batches: PalletBatch[];
  position?: Position & {
    rack?: {
      id: string;
      code: string;
      full_code: string;
      hallway?: {
        id: string;
        code: string;
        full_code: string;
        warehouse?: {
          id: string;
          code: string;
          name: string;
        };
      };
    };
  };
}

export interface PalletStatistics {
  total: number;
  stored: number;
  partial: number;
  empty: number;
  mixed: number;
  totalBoxes: number;
}

export const palletService = {
  /**
   * Get list of pallets with optional filters
   */
  async getPallets(filters?: PalletFilters): Promise<Pallet[]> {
    let query = supabase
      .from("wms_pallets")
      .select(
        `
        *,
        product:products(id, short_name),
        position:wms_positions(
          id, code, full_code, barcode, level,
          rack:wms_racks(
            id, code, full_code,
            hallway:wms_hallways(
              id, code, full_code, warehouse_id
            )
          )
        )
      `
      )
      .order("created_at", { ascending: false });

    if (filters?.status) {
      query = query.eq("status", filters.status);
    }

    if (filters?.pallet_type) {
      query = query.eq("pallet_type", filters.pallet_type);
    }

    if (filters?.position_id) {
      query = query.eq("position_id", filters.position_id);
    }

    if (filters?.product_id) {
      query = query.eq("product_id", filters.product_id);
    }

    if (filters?.has_stock === true) {
      query = query.gt("current_box_count", 0);
    } else if (filters?.has_stock === false) {
      query = query.eq("current_box_count", 0);
    }

    if (filters?.search) {
      query = query.or(
        `code.ilike.%${filters.search}%,barcode.ilike.%${filters.search}%`
      );
    }

    if (filters?.warehouse_id) {
      query = query.not("position_id", "is", null);
    }

    const { data, error } = await query;
    if (error) throw error;

    // Filter by warehouse_id after query if needed (due to nested join)
    let result = (data || []) as Pallet[];
    if (filters?.warehouse_id) {
      result = result.filter((p) => {
        const warehouseId = (p.position as any)?.rack?.hallway?.warehouse_id;
        return warehouseId === filters.warehouse_id;
      });
    }

    return result;
  },

  /**
   * Get single pallet by ID with all details including batches
   */
  async getPalletById(id: string): Promise<PalletWithDetails> {
    const { data: pallet, error: palletError } = await supabase
      .from("wms_pallets")
      .select(
        `
        *,
        product:products(id, short_name),
        position:wms_positions(
          id, code, full_code, barcode, level,
          rack:wms_racks(
            id, code, full_code,
            hallway:wms_hallways(
              id, code, full_code,
              warehouse:wms_warehouses(id, code, name)
            )
          )
        )
      `
      )
      .eq("id", id)
      .single();

    if (palletError) throw palletError;

    // Get batches for this pallet
    const { data: batches, error: batchesError } = await supabase
      .from("wms_pallet_batches")
      .select(
        `
        *,
        product:products(id, short_name),
        production_batch:production_batches(id, batch_number, expiry_date)
      `
      )
      .eq("pallet_id", id)
      .order("added_at", { ascending: false });

    if (batchesError) throw batchesError;

    return {
      ...pallet,
      batches: (batches || []) as PalletBatch[],
    } as PalletWithDetails;
  },

  /**
   * Get pallet by barcode (for scanning)
   */
  async getPalletByBarcode(barcode: string): Promise<PalletWithDetails | null> {
    const { data: pallet, error: palletError } = await supabase
      .from("wms_pallets")
      .select(
        `
        *,
        product:products(id, short_name),
        position:wms_positions(
          id, code, full_code, barcode, level,
          rack:wms_racks(
            id, code, full_code,
            hallway:wms_hallways(
              id, code, full_code,
              warehouse:wms_warehouses(id, code, name)
            )
          )
        )
      `
      )
      .eq("barcode", barcode)
      .maybeSingle();

    if (palletError) throw palletError;
    if (!pallet) return null;

    // Get batches for this pallet
    const { data: batches, error: batchesError } = await supabase
      .from("wms_pallet_batches")
      .select(
        `
        *,
        product:products(id, short_name),
        production_batch:production_batches(id, batch_number, expiry_date)
      `
      )
      .eq("pallet_id", pallet.id)
      .order("added_at", { ascending: false });

    if (batchesError) throw batchesError;

    return {
      ...pallet,
      batches: (batches || []) as PalletBatch[],
    } as PalletWithDetails;
  },

  /**
   * Get pallet by code
   */
  async getPalletByCode(code: string): Promise<PalletWithDetails | null> {
    const { data: pallet, error: palletError } = await supabase
      .from("wms_pallets")
      .select(
        `
        *,
        product:products(id, short_name),
        position:wms_positions(
          id, code, full_code, barcode, level,
          rack:wms_racks(
            id, code, full_code,
            hallway:wms_hallways(
              id, code, full_code,
              warehouse:wms_warehouses(id, code, name)
            )
          )
        )
      `
      )
      .eq("code", code)
      .maybeSingle();

    if (palletError) throw palletError;
    if (!pallet) return null;

    // Get batches for this pallet
    const { data: batches, error: batchesError } = await supabase
      .from("wms_pallet_batches")
      .select(
        `
        *,
        product:products(id, short_name),
        production_batch:production_batches(id, batch_number, expiry_date)
      `
      )
      .eq("pallet_id", pallet.id)
      .order("added_at", { ascending: false });

    if (batchesError) throw batchesError;

    return {
      ...pallet,
      batches: (batches || []) as PalletBatch[],
    } as PalletWithDetails;
  },

  /**
   * Search pallets by code or barcode
   */
  async searchPallets(query: string, limit = 10): Promise<Pallet[]> {
    const { data, error } = await supabase
      .from("wms_pallets")
      .select(
        `
        *,
        product:products(id, short_name),
        position:wms_positions(id, code, full_code)
      `
      )
      .or(`code.ilike.%${query}%,barcode.ilike.%${query}%`)
      .limit(limit);

    if (error) throw error;
    return (data || []) as Pallet[];
  },

  /**
   * Get all pallets at a specific position
   */
  async getPalletsAtPosition(positionId: string): Promise<Pallet[]> {
    const { data, error } = await supabase
      .from("wms_pallets")
      .select(
        `
        *,
        product:products(id, short_name)
      `
      )
      .eq("position_id", positionId)
      .order("received_at", { ascending: false });

    if (error) throw error;
    return (data || []) as Pallet[];
  },

  /**
   * Add a batch to an existing pallet (for mixed pallets)
   */
  async addBatchToPallet(data: PalletBatchFormData): Promise<PalletBatch> {
    const { data: batch, error } = await supabase
      .from("wms_pallet_batches")
      .insert(data)
      .select(
        `
        *,
        product:products(id, short_name),
        production_batch:production_batches(id, batch_number, expiry_date)
      `
      )
      .single();

    if (error) throw error;
    return batch as PalletBatch;
  },

  /**
   * Update box count for a batch on a pallet
   */
  async updateBatchBoxCount(
    batchId: string,
    boxCount: number
  ): Promise<PalletBatch> {
    const { data, error } = await supabase
      .from("wms_pallet_batches")
      .update({ box_count: boxCount })
      .eq("id", batchId)
      .select(
        `
        *,
        product:products(id, short_name),
        production_batch:production_batches(id, batch_number, expiry_date)
      `
      )
      .single();

    if (error) throw error;
    return data as PalletBatch;
  },

  /**
   * Remove a batch from a pallet
   */
  async removeBatchFromPallet(batchId: string): Promise<void> {
    const { error } = await supabase
      .from("wms_pallet_batches")
      .delete()
      .eq("id", batchId);

    if (error) throw error;
  },

  /**
   * Relocate a pallet to a new position
   * This moves the entire pallet with all its batches
   */
  async relocatePallet(
    palletId: string,
    toPositionId: string,
    userId: string
  ): Promise<RelocationResult> {
    // 1. Get current pallet with position
    const { data: pallet, error: palletError } = await supabase
      .from("wms_pallets")
      .select("id, position_id, current_box_count, product_id")
      .eq("id", palletId)
      .single();

    if (palletError) throw palletError;

    const fromPositionId = pallet.position_id;

    // 2. Update pallet position
    const { error: updateError } = await supabase
      .from("wms_pallets")
      .update({
        position_id: toPositionId,
        updated_at: new Date().toISOString(),
      })
      .eq("id", palletId);

    if (updateError) throw updateError;

    // 3. Update inventory records - move from old position to new
    if (fromPositionId) {
      // Update existing inventory records to new position
      const { error: inventoryError } = await supabase
        .from("wms_inventory")
        .update({
          position_id: toPositionId,
          updated_at: new Date().toISOString(),
        })
        .eq("pallet_id", palletId)
        .eq("position_id", fromPositionId);

      if (inventoryError) throw inventoryError;
    }

    // 4. Record the movement
    const { data: movement, error: movementError } = await supabase
      .from("wms_movements")
      .insert({
        movement_type: "transfer",
        pallet_id: palletId,
        product_id: pallet.product_id,
        box_quantity: pallet.current_box_count,
        from_position_id: fromPositionId,
        to_position_id: toPositionId,
        reference_type: "manual",
        reason: "Pallet relocation",
        performed_by: userId,
        performed_at: new Date().toISOString(),
      })
      .select("id")
      .single();

    if (movementError) throw movementError;

    return {
      success: true,
      pallet_id: palletId,
      from_position_id: fromPositionId || undefined,
      to_position_id: toPositionId,
      movement_id: movement.id,
    };
  },

  /**
   * Get position by barcode (for destination scanning)
   */
  async getPositionByBarcode(barcode: string): Promise<Position | null> {
    const { data, error } = await supabase
      .from("wms_positions")
      .select(
        `
        *,
        rack:wms_racks(
          id, code, full_code,
          hallway:wms_hallways(
            id, code, full_code,
            warehouse:wms_warehouses(id, code, name)
          )
        )
      `
      )
      .eq("barcode", barcode)
      .maybeSingle();

    if (error) throw error;
    return data as Position | null;
  },

  /**
   * Get position by full code
   */
  async getPositionByFullCode(fullCode: string): Promise<Position | null> {
    const { data, error } = await supabase
      .from("wms_positions")
      .select(
        `
        *,
        rack:wms_racks(
          id, code, full_code,
          hallway:wms_hallways(
            id, code, full_code,
            warehouse:wms_warehouses(id, code, name)
          )
        )
      `
      )
      .eq("full_code", fullCode)
      .maybeSingle();

    if (error) throw error;
    return data as Position | null;
  },

  /**
   * Get pallet statistics
   */
  async getPalletStatistics(warehouseId?: string): Promise<PalletStatistics> {
    let query = supabase
      .from("wms_pallets")
      .select("id, status, pallet_type, current_box_count, position_id");

    const { data, error } = await query;
    if (error) throw error;

    const pallets = data || [];

    // If warehouse filter, we need to check positions
    let filteredPallets = pallets;
    if (warehouseId) {
      const positionIds = pallets
        .filter((p) => p.position_id)
        .map((p) => p.position_id as string);

      if (positionIds.length > 0) {
        const { data: positions, error: posError } = await supabase
          .from("wms_positions")
          .select(
            `
            id,
            rack:wms_racks!inner(
              hallway:wms_hallways!inner(warehouse_id)
            )
          `
          )
          .in("id", positionIds);

        if (posError) throw posError;

        const validPositionIds = new Set(
          (positions || [])
            .filter((p: any) => p.rack?.hallway?.warehouse_id === warehouseId)
            .map((p) => p.id)
        );

        filteredPallets = pallets.filter(
          (p) => !p.position_id || validPositionIds.has(p.position_id)
        );
      }
    }

    return {
      total: filteredPallets.length,
      stored: filteredPallets.filter((p) => p.status === "stored").length,
      partial: filteredPallets.filter((p) => p.status === "partial").length,
      empty: filteredPallets.filter((p) => p.status === "empty").length,
      mixed: filteredPallets.filter((p) => p.pallet_type === "mixed").length,
      totalBoxes: filteredPallets.reduce(
        (sum, p) => sum + (p.current_box_count || 0),
        0
      ),
    };
  },

  /**
   * Get pallet movement history
   */
  async getPalletMovements(palletId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from("wms_movements")
      .select(
        `
        *,
        from_position:wms_positions!from_position_id(id, code, full_code),
        to_position:wms_positions!to_position_id(id, code, full_code),
        performer:profiles!performed_by(id, name, lastname)
      `
      )
      .eq("pallet_id", palletId)
      .order("performed_at", { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Update pallet status
   */
  async updatePalletStatus(
    palletId: string,
    status: PalletStatus
  ): Promise<Pallet> {
    const { data, error } = await supabase
      .from("wms_pallets")
      .update({ status, updated_at: new Date().toISOString() })
      .eq("id", palletId)
      .select()
      .single();

    if (error) throw error;
    return data as Pallet;
  },
};
